<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import Icon from 'next/icon/Icon.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  additionalAttributes,
  contentAttributes,
} = useMessageContext();

const scheduledOriginId = computed(
  () => contentAttributes.value?.lixfollowOriginScheduleId
);

const isHiddenComment = computed(
  () =>
    contentAttributes.value?.feedMessageType === 'comment' &&
    contentAttributes.value?.hidden
);
const { t } = useI18n();

const variantBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.USER_COMMENT]: 'bg-n-teal-3 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.ACTIVITY_POST]: 'bg-n-teal-3 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.CARD]: 'text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
  [MESSAGE_VARIANTS.SCHEDULE]:
    'bg-n-solid-amber text-n-amber-12 border-l-4 border-l-n-amber-12/40',
};

const trackingData = additionalAttributes.value?.trackingData;

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [variantBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  if (trackingData) {
    classToApply.push('relative pt-3 min-w-60');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    ![MESSAGE_VARIANTS.ACTIVITY, MESSAGE_VARIANTS.ACTIVITY_POST].includes(
      variant
    )
);

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="trackingData"
      v-tooltip.top="
        $t('CHAT_LIST.TRACKING_FEATURE', {
          name: trackingData.feature,
          usedTimes: trackingData.usedTimes || 'no-data',
        })
      "
      class="cursor-help absolute -top-1 right-2 z-10"
    >
      <span
        class="bg-woot-200 text-woot-700 text-center items-center flex justify-center px-2 rounded-full px-3 py-0.5 text-xs shadow"
      >
        <Icon icon="i-lucide-book-a" class="flex size-2 me-2" />
        {{ trackingData.name }}
      </span>
    </div>

    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <div
      v-if="scheduledOriginId"
      v-tooltip.top="$t('SCHEDULE_BUBBLE.SENT_FROM_SCHEDULE')"
      class="-mt-1 mb-1.5 flex items-center gap-1 text-xs opacity-70"
      data-test="scheduled-origin-annotation"
    >
      <Icon icon="i-lucide-clock" class="size-3" />
      <span>{{ $t('SCHEDULE_BUBBLE.SCHEDULED_LABEL') }}</span>
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
      ]"
      class="my-2"
    />
    <div
      v-if="isHiddenComment"
      class="flex items-center gap-1 text-xs text-n-slate-11"
      :class="flexOrientationClass"
    >
      <Icon icon="i-lucide-eye-off" class="size-3" />
      <span>{{ $t('CONVERSATION.HIDDEN_COMMENT_LABEL') }}</span>
    </div>
  </div>
</template>
